"use client";

import { useEffect, useRef, useState } from "react";
import ProfileItem from "./profile-item";
import type Profile from "../types/profile";

interface ProfileWheelProps {
  profiles: Profile[];
  winner: Profile | null;
  isRolling: boolean;
  reset: boolean;
  onAnimationComplete?: () => void; // Added callback prop
}

export default function ProfileWheel({
  profiles,
  winner,
  isRolling,
  reset,
  onAnimationComplete,
}: Readonly<ProfileWheelProps>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [translateX, setTranslateX] = useState(0);
  const [initialRender, setInitialRender] = useState(true);
  const [targetIndex, setTargetIndex] = useState(0);

  const duplicatedProfiles = [
    ...profiles,
    ...profiles,
    ...profiles,
    ...profiles,
    ...profiles,
    ...profiles,
    ...profiles,
    ...profiles,
    ...profiles,
  ];

  // Handle initial render
  useEffect(() => {
    if (initialRender) {
      setInitialRender(false);
    }
  }, [initialRender]);

  // Reset position when reset prop changes
  useEffect(() => {
    setTranslateX(0);
  }, [reset]);

  // Handle animation completion
  useEffect(() => {
    if (isRolling && winner) {
      const timer = setTimeout(() => {
        onAnimationComplete?.();
      }, 8000); // Match this with your CSS animation duration

      return () => clearTimeout(timer);
    }
  }, [isRolling, winner, onAnimationComplete]);

  // Main wheel animation effect
  useEffect(() => {
    if (!winner || !containerRef.current || !profiles.length || initialRender)
      return;

    // Find the winner's index in the original array
    const winnerIndexInOriginal = profiles.findIndex((p) => p.id === winner.id);
    if (winnerIndexInOriginal === -1) return;

    // Target the middle set of profiles
    const targetSetIndex = 4;
    const targetIndex =
      targetSetIndex * profiles.length + winnerIndexInOriginal;
    setTargetIndex(targetIndex);

    // Calculate item width based on the first profile item
    const itemElements =
      containerRef.current.querySelectorAll("[data-profile-id]");
    if (itemElements.length === 0) return;

    const firstItem = itemElements[0] as HTMLElement;
    const itemWidth =
      firstItem.offsetWidth +
      parseFloat(getComputedStyle(firstItem).marginLeft) +
      parseFloat(getComputedStyle(firstItem).marginRight);

    // Calculate container width
    const containerWidth = containerRef.current.clientWidth;

    const getRandomNumber = () => {
      return Math.floor(Math.random() * (50 - -15 + 1)) + -15;
    };

    const targetPosition =
      targetIndex * itemWidth -
      (containerWidth - itemWidth) / 2 +
      getRandomNumber();

    // Set the new translation with smooth animation
    setTranslateX(-targetPosition);
  }, [winner, profiles, initialRender]);

  return (
    <div className="relative w-[1875px] overflow-hidden border border-[rgba(255,255,255,0.15)] rounded-3xl bg-[#1a1a1a] shadow-md">
      <div className="absolute top-0 bottom-0 left-1/2 w-1 bg-purple-500 z-20 transform -translate-x-1/2">
        <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-purple-500"></div>
        <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-purple-500"></div>
      </div>

      <div
        ref={containerRef}
        className="flex py-6 px-4"
        style={{
          transform: `translateX(${translateX}px)`,
          transition:
            isRolling && !initialRender
              ? "transform 8s cubic-bezier(0.0125, 0.1, 0.1, 1)"
              : "none",
        }}
      >
        {duplicatedProfiles.map((profile, index) => {
          const isWinner = winner ? index === targetIndex : false;

          return (
            <div
              key={`${profile.id}-${index}`}
              className="flex-shrink-0"
              data-profile-id={profile.id}
              data-index={index}
            >
              <ProfileItem profile={profile} isWinner={isWinner} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
